/** Phase D texts (sky): weather, calendar, typhoons, earthquakes and Mom's calls. */
export const enSky = {
  sky: {
    mapNames: {} as Record<string, string>,
    zones: {} as Record<string, string>,
    objects: {
      sky_calendar: 'Calendar',
      sky_kit: 'Emergency kit',
      sky_umbrellas: 'Umbrellas',
    },
    npc: {} as Record<string, string>,
    items: {} as Record<string, { name: string; desc: string }>,

    kinds: {
      sunny: 'Sunny',
      cloudy: 'Cloudy',
      rain: 'Rain',
      storm: 'Thunderstorm',
      cool: 'Cold front',
      typhoon: 'Typhoon',
    },
    hud: {
      /** Tooltip on the time box. */
      title: (week: number, date: string, kind: string) => `Week ${week} · ${date} · ${kind}`,
    },

    forecast: {
      title: 'Weather forecast',
      today: 'Today',
      tomorrow: 'Tomorrow',
      rain: (p: number) => `☂ ${p}%`,
      /** Replaces the news ticker's fixed weather line with today's real forecast. */
      tickerToday: (kind: string, lo: number, hi: number, p: number) => `Weather: ${kind}, ${lo}–${hi}°C, chance of rain ${p}% · Remember: check the source before you share ·`,
      range: (lo: number, hi: number) => `${lo}–${hi}°C`,
      showers: (from: string, to: string) => `Showers ${from}–${to}`,
      alert: '🌀 Typhoon warning! Tomorrow is a typhoon day off: no school, no work. Stay indoors!',
      ticker: '🌀 TYPHOON WARNING · Sea and land warnings issued · Tomorrow: typhoon day off · Stock up on water and charge your phone ·',
      tips: {
        sunny: 'Hot and sunny: drink water and wear a hat.',
        cloudy: 'Cloudy skies, maybe a quick shower.',
        rain: 'Rainy: take an umbrella!',
        storm: 'Afternoon thunderstorms: take an umbrella and stay away from tall trees.',
        cool: 'A cold front is here: wear a jacket.',
        typhoon: 'Typhoon! Stay at home and keep away from windows.',
      },
    },

    toasts: {
      typhoonEve: '🌀 Typhoon warning: tomorrow is a typhoon day off (颱風假)! Get ready at home.',
      typhoonDay: '🌀 Typhoon day off! School and shops are closed. Stay safe indoors.',
      typhoonPassed: '🌤️ The typhoon has passed. The doors are open again.',
      doorClosed: 'Closed for the typhoon. Stay safe indoors!',
      tooWindy: 'The wind is far too strong. It isn’t safe to go out in a typhoon!',
      rainStarts: '🌧️ It’s starting to rain!',
      stormStarts: '⛈️ Thunder! A storm is coming.',
      soaked: '💦 You got soaked in the rain! Buy an umbrella at the store.',
      umbrellaOpen: '☂️ You open your umbrella. Nice and dry!',
      sentHome: '🌀 Typhoon day off: no school today. You stay at home.',
      windy: '🍃 The typhoon’s wind is howling outside!',
      quakeAlert: '📳 Earthquake early warning (地震速報)! Strong shaking soon — protect your head!',
    },

    calendar: {
      who: 'Calendar',
      today: (date: string, kind: string, temp: number, pct: number) => `Today is **${date}**. ${kind}, ${temp}°C, chance of rain ${pct}%.`,
      tomorrow: (kind: string, lo: number, hi: number, pct: number) => `Tomorrow: ${kind}, ${lo}–${hi}°C, rain ${pct}%.`,
      tear: 'You tear off yesterday’s page. Tear-off calendars like this hang in many Taiwanese homes.',
      practice: 'Say it in English',
      // English practice lines.
      sayDate: (date: string) => `Today is ${date}.`,
      say: {
        sunny: 'It’s sunny and hot today.',
        cloudy: 'It’s cloudy today.',
        rain: 'It’s rainy today. I need an umbrella.',
        storm: 'There will be a thunderstorm this afternoon.',
        cool: 'It’s cold today. I need a jacket.',
        typhoon: 'A typhoon is coming. Stay at home!',
      },
    },

    kit: {
      title: '🌀 Typhoon checklist',
      intro: 'Get the house ready before the storm gets worse. Tap each job when it’s done.',
      calm: 'Our emergency kit: water, a flashlight, batteries and a first-aid box. Ready for typhoons and earthquakes!',
      calmTomorrow: 'A typhoon comes tomorrow. Tomorrow morning, get the house ready with this checklist.',
      done: '✅ Done',
      go: 'Do it',
      goBalcony: 'Go to the balcony',
      all: 'Everything is ready. Well done, typhoon expert!',
      items: {
        // English practice lines (the `en` fields).
        water: { icon: '💧', label: 'Fill water bottles and the bathtub', en: 'Fill the water bottles.' },
        phone: { icon: '🔋', label: 'Charge the phone and power bank', en: 'Charge the phone.' },
        plants: { icon: '🪴', label: 'Bring the balcony plants inside', en: 'Bring the plants inside.' },
        flashlight: { icon: '🔦', label: 'Find the flashlight and batteries', en: 'Where is the flashlight?' },
      },
      balconyAsk: 'The wind is howling! The flower pots could fly off the balcony. Bring them inside?',
      balconyYes: '🪴 Carry them in',
      balconyNo: 'Later',
      balconyDone: 'The pots are safe inside.',
    },

    typhoonMorning: {
      who: 'Mom',
      text: 'Good morning! The typhoon is here, so today is a typhoon day off: no school and no work. Will you help me get the house ready? The checklist is on the emergency kit by the door.',
      good: '“Sure, Mom! I’ll fill the water and charge the phone.”',
      goodMsg: 'Mom smiles: “My little helper!”',
      bad: '“A day off? Yay, I’m going to play outside!”',
      badMsg: 'Mom frowns: “Absolutely not! It’s dangerous out there.”',
    },

    quake: {
      who: 'Earthquake!',
      text: 'The ground is shaking! Things are rattling and falling. What should you do?',
      drop: '🫣 Drop to the floor, cover your head, hold on',
      outside: '🏃 Run outside as fast as you can',
      lift: '🛗 Take the lift down',
      window: '🪟 Stand by the window and watch',
      right: 'That’s right! Drop down, get under a table, cover your head and hold on until the shaking stops.',
      wrongOutside: 'Not safe! Running while it shakes, you can fall or be hit by falling glass and signs.',
      wrongLift: 'Never take a lift in an earthquake: it can stop and trap you. Use the stairs after the shaking stops.',
      wrongWindow: 'Windows can break! Keep away from glass.',
      // English practice line.
      phrase: 'Drop, cover and hold on!',
      lesson: 'Say it in English: **“Drop, cover and hold on!”** Taiwan has many earthquakes, so everyone practises this at school.',
      after: 'The shaking stopped. Phew… everyone is OK.',
      momWho: 'Mom',
      momHome: 'Mom hurries over: “Are you all right? Did anything fall on you?”',
      momGood: '“I’m OK, Mom! I did drop, cover and hold on.”',
      momGoodMsg: 'Mom hugs you tight.',
      momBad: '“It was nothing, stop worrying.”',
      momBadMsg: 'Mom sighs, still shaky.',
    },

    umbrella: {
      useRain: '☂️ You open your umbrella. The rain drums on top of it.',
      useDry: 'It isn’t raining now. You keep the umbrella in your bag.',
      who: 'Umbrella stand',
      ask: (price: number) => `A bucket of umbrellas by the door, like every convenience store in Taiwan. One costs ${price} coins.`,
      buy: (price: number) => `☂️ Buy one (${price}🪙)`,
      no: 'No, thanks',
      poor: 'Not enough coins…',
      bought: '☂️ Got an umbrella! Rain can’t stop you now.',
      have: 'You already have an umbrella in your bag.',
    },

    calls: {
      momTyphoon: {
        caller: 'Mom ❤️',
        desc: 'Typhoon worries',
        intro: 'Sweetheart, the typhoon is getting close! Where are you? Please come home before the wind gets strong, and stay away from the river.',
        good: '“I’m coming home now, Mom. I’ll help you get ready!”',
        goodMsg: 'Mom sounds relieved: “Thank you, darling. Be careful!”',
        bad: '“It’s only a bit of wind. I’ll stay out.”',
        badMsg: 'Mom’s voice gets tight with worry…',
      },
      momQuake: {
        caller: 'Mom ❤️',
        desc: 'Calling right after the earthquake',
        intro: 'Did you feel that earthquake? Are you hurt? Where are you right now?',
        good: '“I’m OK, Mom! I did drop, cover and hold on.”',
        goodMsg: 'Mom breathes again: “Good! That’s exactly right.”',
        bad: '“Ugh, Mom, it was nothing.”',
        badMsg: 'Mom says quietly: “I was just scared for you…”',
      },
      momRain: {
        caller: 'Mom ❤️',
        desc: 'It’s raining',
        intro: 'It’s raining outside! Did you take an umbrella? Don’t get wet and catch a cold.',
        good: '“Thanks, Mom! I’ll get an umbrella at the store.”',
        goodMsg: 'Mom: “Good kid. Dry your hair when you get home!”',
        bad: '“Rain is fine, I don’t care.”',
        badMsg: 'Mom sighs: “Then don’t come home sneezing…”',
      },
    },

    journal: {
      typhoonReady: 'Got the house ready for a typhoon',
      quakeSafe: 'Stayed safe in an earthquake: drop, cover and hold on!',
    },
  },
};
